import React, { useState } from 'react';
import axios from 'axios';
import RestaurantCard from './FilterRestaurantCard';
import LocationSearch from './LocationSearch.js';
import CurrentLocationButton from './CurrentLocationButton';
import { MapPin, DollarSign, Navigation, Star } from 'lucide-react';
import { API_BASE_URL } from '../config';

function FilterPage() {
  const [maxPrice, setMaxPrice] = useState(4);
  const [cuisineType, setCuisineType] = useState('all');
  const [restaurants, setRestaurants] = useState([]);
  const [cuisines] = useState(['Italian', 'Japanese', 'Mexican', 'Indian', 'American', 'Thai', 'Chinese']);
  const [searchLocation, setSearchLocation] = useState('40.1106,-88.2073');
  const [currLocation, setCurrLocation] = useState('');
  const [useCurrLocation, setUseCurrLocation] = useState(false);
  const [formattedAddress, setFormattedAddress] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [radiusMiles, setRadiusMiles] = useState(0.5);
  const [openNow, setOpenNow] = useState(true);
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [chosenRestaurant, setChosenRestaurant] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkNumber] = useState(() => Math.floor(100000 + Math.random() * 900000));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setRestaurants([]);
    setSelectedRestaurants([]);

    const radiusInMeters = radiusMiles * 1609.34;

    let loc = '';
    if (useCurrLocation && currLocation) {
      loc = currLocation;
    } else if (searchLocation) {
      loc = searchLocation;
    }

    if (!loc || loc.trim() === '') {
      setError('Please select a location or use your current location');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/restaurants/random`, {
        params: {
          cuisine: cuisineType === 'all' ? '' : cuisineType,
          minPrice: 1,
          maxPrice: maxPrice,
          location: loc,
          radius: radiusInMeters,
          openNow: openNow,
        },
      });

      const restaurants = response.data.restaurants || response.data;

      if (!restaurants || restaurants.length === 0) {
        setError('No restaurants found. Try expanding your search radius or changing your filters.');
        setRestaurants([]);
        return;
      }

      if (restaurants.length < 5) {
        setError(`Only found ${restaurants.length} restaurants. Please expand your search radius or change your filters to find at least 5 restaurants.`);
        setRestaurants([]);
        return;
      }

      const restaurantsWithIds = restaurants.map((restaurant, index) => ({
        ...restaurant,
        id: `${restaurant.name}-${index}`
      }));

      setRestaurants(restaurantsWithIds);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      if (error.response) {
        let errorMessage = 'Failed to fetch restaurants';

        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. There might not be enough restaurants matching your filters. Try expanding your radius or changing filters.';
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid search parameters. Please check your location and filters.';
        }

        setError(errorMessage);
      } else if (error.request) {
        setError('Unable to reach the server. Please make sure the backend is running.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectRestaurant = (restaurantId) => {
    setSelectedRestaurants((prevSelected) => {
      if (prevSelected.includes(restaurantId)) {
        // Deselect
        return prevSelected.filter((id) => id !== restaurantId);
      } else if (prevSelected.length < 3) {
        // Select (under limit)
        return [...prevSelected, restaurantId];
      } else {
        // At limit - show warning
        setError('You can only select 3 restaurants. Please deselect one to choose another.');
        setTimeout(() => setError(null), 3000); // Clear error after 3 seconds
        return prevSelected;
      }
    });
  };

  const submitSelection = () => {
    const selectedRestaurantObjects = selectedRestaurants.map(id =>
      restaurants.find(r => r.id === id)
    );

    const randomIndex = Math.floor(Math.random() * selectedRestaurantObjects.length);
    const chosenOne = selectedRestaurantObjects[randomIndex];

    setChosenRestaurant(chosenOne);
  };

  const reset = () => {
    setRestaurants([]);
    setSelectedRestaurants([]);
    setChosenRestaurant(null);
    setError(null);
    // Keep location state unchanged
  };

  const renderPriceLevel = (level) => {
    if (!level) return '';
    return '$'.repeat(level);
  };

  // Step 0: No restaurants yet - show search form
  if (!chosenRestaurant && restaurants.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white border-2 border-blue-900 shadow-2xl">
          {/* Guest Check Header */}
          <div className="border-b-2 border-blue-900 p-3 bg-white">
            <div className="grid grid-cols-5 gap-2 text-xs text-blue-900 border-2 border-blue-900 mb-3 font-medium">
              <div className="border-r border-blue-900 p-2">Date: 12/15/24</div>
              <div className="border-r border-blue-900 p-2">Amount: $$$</div>
              <div className="border-r border-blue-900 p-2">Guests: 1</div>
              <div className="border-r border-blue-900 p-2">Server: 5-3-1</div>
              <div className="p-2 text-red-600 font-bold text-lg">{checkNumber}</div>
            </div>
            <h1 className="text-5xl text-blue-900 text-center mb-3" style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold' }}>
              Guest Check
            </h1>
            <div className="grid grid-cols-5 gap-2 text-xs text-blue-900 border-2 border-blue-900 font-medium">
              <div className="border-r border-blue-900 p-2">Date: 12/15/24</div>
              <div className="border-r border-blue-900 p-2">Table: 1</div>
              <div className="border-r border-blue-900 p-2">Guests: 1</div>
              <div className="border-r border-blue-900 p-2">Server: 5-3-1</div>
              <div className="p-2 text-red-600 font-bold text-lg">{checkNumber}</div>
            </div>
          </div>

          <div className="p-8 bg-green-50">
            <div className="text-center mb-8">
              <p className="text-gray-600 text-lg">Take a chance and find your new favorite spot!</p>
            </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-blue-900 mb-2 uppercase text-left">
                TABLE LOCATION:
              </label>
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <LocationSearch
                    searchLocation={searchLocation}
                    setSearchLocation={setSearchLocation}
                    setUseCurrLocation={setUseCurrLocation}
                    formattedAddress={formattedAddress}
                    setFormattedAddress={setFormattedAddress}
                    selectedPlace={selectedPlace}
                    setSelectedPlace={setSelectedPlace}
                  />
                </div>
                <CurrentLocationButton setCurrLocation={setCurrLocation} setUseCurrLocation={setUseCurrLocation} useCurrLocation={useCurrLocation} setFormattedAddress={setFormattedAddress} setSelectedPlace={setSelectedPlace} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-blue-900 mb-2 uppercase text-left">
                SEARCH RADIUS: {radiusMiles.toFixed(1)} miles
              </label>
              <input
                type="range"
                min="0.1"
                max="25"
                step="0.1"
                value={radiusMiles}
                onChange={(e) => setRadiusMiles(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-900"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-blue-900 mb-2 uppercase text-left">
                CUISINE TYPE
              </label>
              <select
                className="w-full px-4 py-3 border-2 border-blue-900 focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                style={{ fontFamily: '"Courier New", monospace', borderRadius: '0' }}
                value={cuisineType}
                onChange={(e) => setCuisineType(e.target.value)}
              >
                <option value="all">All Cuisines</option>
                {cuisines.map((cuisine) => (
                  <option key={cuisine} value={cuisine.toLowerCase()}>
                    {cuisine}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-blue-900 mb-2 uppercase text-left">
                MAX PRICE:
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMaxPrice(0)}
                  className={`flex-1 py-2 border-2 border-blue-900 font-bold transition-colors ${
                    maxPrice === 0
                      ? 'bg-blue-900 text-white'
                      : 'bg-white text-blue-900 hover:bg-gray-100'
                  }`}
                >
                  ANY
                </button>
                <button
                  type="button"
                  onClick={() => setMaxPrice(1)}
                  className={`flex-1 py-2 border-2 border-blue-900 font-bold transition-colors ${
                    maxPrice === 1
                      ? 'bg-blue-900 text-white'
                      : 'bg-white text-blue-900 hover:bg-gray-100'
                  }`}
                >
                  $
                </button>
                <button
                  type="button"
                  onClick={() => setMaxPrice(2)}
                  className={`flex-1 py-2 border-2 border-blue-900 font-bold transition-colors ${
                    maxPrice === 2
                      ? 'bg-blue-900 text-white'
                      : 'bg-white text-blue-900 hover:bg-gray-100'
                  }`}
                >
                  $$
                </button>
                <button
                  type="button"
                  onClick={() => setMaxPrice(3)}
                  className={`flex-1 py-2 border-2 border-blue-900 font-bold transition-colors ${
                    maxPrice === 3
                      ? 'bg-blue-900 text-white'
                      : 'bg-white text-blue-900 hover:bg-gray-100'
                  }`}
                >
                  $$$
                </button>
                <button
                  type="button"
                  onClick={() => setMaxPrice(4)}
                  className={`flex-1 py-2 border-2 border-blue-900 font-bold transition-colors ${
                    maxPrice === 4
                      ? 'bg-blue-900 text-white'
                      : 'bg-white text-blue-900 hover:bg-gray-100'
                  }`}
                >
                  $$$$
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="openNow"
                checked={openNow}
                onChange={(e) => setOpenNow(e.target.checked)}
                className="w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
              />
              <label htmlFor="openNow" className="ml-2 text-sm text-gray-700">
                Only show restaurants open now
              </label>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
                <strong>Error:</strong> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 font-semibold text-lg transition-all ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-900 text-white hover:bg-blue-800'
              }`}
            >
              {isLoading ? 'Loading...' : '[ FIND 5 RESTAURANTS ]'}
            </button>
          </form>

          {isLoading && (
            <div className="mt-6 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-900 border-r-transparent"></div>
              <p className="mt-2 text-gray-600">Finding restaurants...</p>
            </div>
          )}
          </div>

          {/* Guest Check Footer */}
          <div className="border-t-2 border-blue-900 p-3 text-center bg-green-50">
            <p className="text-xs text-gray-600">Thank you for your indecision!</p>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Select 3 from 5
  if (!chosenRestaurant && restaurants.length > 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full bg-white border-2 border-blue-900 shadow-2xl">
          {/* Guest Check Header */}
          <div className="border-b-2 border-blue-900 p-3 bg-white">
            <div className="grid grid-cols-5 gap-2 text-xs text-blue-900 border-2 border-blue-900 mb-3 font-medium">
              <div className="border-r border-blue-900 p-2">Date: 12/15/24</div>
              <div className="border-r border-blue-900 p-2">Amount: $$$</div>
              <div className="border-r border-blue-900 p-2">Guests: 1</div>
              <div className="border-r border-blue-900 p-2">Server: 5-3-1</div>
              <div className="p-2 text-red-600 font-bold text-lg">{checkNumber}</div>
            </div>
            <h1 className="text-5xl text-blue-900 text-center mb-3" style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold' }}>
              Guest Check
            </h1>
            <div className="grid grid-cols-5 gap-2 text-xs text-blue-900 border-2 border-blue-900 mb-2 font-medium">
              <div className="border-r border-blue-900 p-2">Date: 12/15/24</div>
              <div className="border-r border-blue-900 p-2">Table: 1</div>
              <div className="border-r border-blue-900 p-2">Guests: 1</div>
              <div className="border-r border-blue-900 p-2">Server: 5-3-1</div>
              <div className="p-2 text-red-600 font-bold text-lg">{checkNumber}</div>
            </div>
            <div className="text-xs text-blue-900 font-semibold py-1 border-t border-blue-900">
              SELECTED: {selectedRestaurants.length}/3
            </div>
          </div>

          <div className="p-6 bg-green-50">
          {error && (
            <div className="bg-red-50 border-2 border-red-600 p-3 text-red-700 text-sm mb-4 text-center">
              {error}
            </div>
          )}

          <div className="mb-6 relative">
            {/* Continuous vertical lines */}
            <div className="absolute left-[80px] top-0 bottom-0 w-[2px] bg-blue-900 pointer-events-none"></div>
            <div className="absolute right-[140px] top-0 bottom-0 w-[2px] bg-blue-900 pointer-events-none"></div>
            <div className="absolute right-[60px] top-0 bottom-0 w-[2px] bg-blue-900 pointer-events-none"></div>

            {restaurants.map((restaurant, index) => (
              <RestaurantCard
                key={restaurant.id}
                restaurant={restaurant}
                isSelected={selectedRestaurants.includes(restaurant.id)}
                onSelect={handleSelectRestaurant}
                isLast={index === restaurants.length - 1}
              />
            ))}
          </div>

          <div className="border-t-2 border-blue-900 pt-4 mt-4 bg-green-50">
            <div className="flex gap-4 justify-center mb-4">
              <button
                onClick={reset}
                className="px-8 py-3 bg-white border-2 border-blue-900 text-blue-900 font-semibold hover:bg-blue-50 transition-colors flex items-center"
              >
                [ VOID ]
              </button>
              <button
                onClick={submitSelection}
                disabled={selectedRestaurants.length !== 3}
                className={`px-8 py-3 font-semibold transition-all flex items-center ${
                  selectedRestaurants.length === 3
                    ? 'bg-blue-900 text-white hover:bg-blue-800'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                [ SETTLE CHECK ]
              </button>
            </div>
          </div>
          </div>

          {/* Guest Check Footer */}
          <div className="border-t-2 border-blue-900 p-3 text-center bg-green-50">
            <p className="text-xs text-gray-600">Thank you for your indecision!</p>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Final Pick
  if (chosenRestaurant) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full bg-white border-2 border-blue-900 shadow-2xl relative">
          {/* PAID Stamp */}
          <div className="absolute top-24 right-8 border-4 border-red-600 text-red-600 font-black text-4xl px-8 py-3 opacity-70 z-10" style={{ transform: 'rotate(-15deg)' }}>
            PAID
          </div>

          {/* Guest Check Header */}
          <div className="border-b-2 border-blue-900 p-3 bg-white">
            <div className="grid grid-cols-5 gap-2 text-xs text-blue-900 border-2 border-blue-900 mb-3 font-medium">
              <div className="border-r border-blue-900 p-2">Date: 12/15/24</div>
              <div className="border-r border-blue-900 p-2">Amount: $$$</div>
              <div className="border-r border-blue-900 p-2">Guests: 1</div>
              <div className="border-r border-blue-900 p-2">Server: 5-3-1</div>
              <div className="p-2 text-red-600 font-bold text-lg">{checkNumber}</div>
            </div>
            <h1 className="text-5xl text-blue-900 text-center mb-3" style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold' }}>
              Guest Check
            </h1>
            <div className="grid grid-cols-5 gap-2 text-xs text-blue-900 border-2 border-blue-900 font-medium">
              <div className="border-r border-blue-900 p-2">Date: 12/15/24</div>
              <div className="border-r border-blue-900 p-2">Table: 1</div>
              <div className="border-r border-blue-900 p-2">Guests: 1</div>
              <div className="border-r border-blue-900 p-2">Server: 5-3-1</div>
              <div className="p-2 text-red-600 font-bold text-lg">{checkNumber}</div>
            </div>
          </div>

          <div className="p-8 bg-green-50">
            {chosenRestaurant.photoUrl && (
              <div className="h-48 overflow-hidden mb-6 border-2 border-blue-900">
                <img
                  src={chosenRestaurant.photoUrl}
                  alt={chosenRestaurant.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Restaurant Name in Handwritten Style */}
            <div className="text-center mb-6">
              <h3 className="text-5xl font-bold text-blue-900 mb-3" style={{ fontFamily: 'Brush Script MT, cursive', transform: 'rotate(-2deg)' }}>
                {chosenRestaurant.name}
              </h3>
              <p className="text-lg text-gray-700">{chosenRestaurant.address || chosenRestaurant.vicinity}</p>
            </div>

            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-8 text-lg text-gray-500 mb-8">
                {chosenRestaurant.rating && (
                  <span className="flex items-center">
                    <Star className="w-6 h-6 text-yellow-500 mr-2 fill-yellow-500" />
                    {chosenRestaurant.rating}
                  </span>
                )}
                {chosenRestaurant.price_level && (
                  <span className="text-2xl">{renderPriceLevel(chosenRestaurant.price_level)}</span>
                )}
              </div>
              {chosenRestaurant.overview && (
                <p className="text-gray-600 mb-6">{chosenRestaurant.overview}</p>
              )}
              {chosenRestaurant.opening_hours && (
                <div className="mb-6">
                  <div className={`text-lg font-semibold mb-2 ${chosenRestaurant.opening_hours.open_now ? 'text-green-600' : 'text-red-600'}`}>
                    {chosenRestaurant.opening_hours.open_now ? 'Open Now' : 'Closed'}
                  </div>
                  {chosenRestaurant.opening_hours.weekday_text && chosenRestaurant.opening_hours.weekday_text.length > 0 && (
                    <div className="text-left max-w-md mx-auto bg-gray-50 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">Hours</h4>
                      <div className="text-sm text-gray-600 space-y-1">
                        {chosenRestaurant.opening_hours.weekday_text.map((day, index) => (
                          <div key={index}>{day}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {chosenRestaurant.website && (
                <a
                  href={chosenRestaurant.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-2 bg-blue-900 text-white font-semibold mr-4 hover:bg-blue-800 transition-colors"
                >
                  Visit Website
                </a>
              )}
              {chosenRestaurant.phone && (
                <a
                  href={`tel:${chosenRestaurant.phone}`}
                  className="inline-block px-6 py-2 bg-blue-900 text-white font-semibold hover:bg-blue-800 transition-colors"
                >
                  {chosenRestaurant.phone}
                </a>
              )}
            </div>

            {/* Totals Grid Section */}
            <div className="border-t-2 border-blue-900 pt-4 mt-6">
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div className="text-left font-semibold text-blue-900">SUBTOTAL:</div>
                <div className="text-right">1 Restaurant</div>
                <div className="text-left font-semibold text-blue-900">TAX:</div>
                <div className="text-right">Fun</div>
                <div className="text-left text-lg font-black text-blue-900">TOTAL:</div>
                <div className="text-right text-lg font-black">Great Experience!</div>
              </div>

              <button
                onClick={reset}
                className="w-full py-3 bg-blue-900 text-white font-semibold text-lg hover:bg-blue-800 transition-colors"
              >
                [ NEW CHECK ]
              </button>
            </div>
          </div>

          {/* Guest Check Footer */}
          <div className="border-t-2 border-blue-900 p-3 text-center bg-green-50">
            <p className="text-xs text-gray-600">Thank you for your indecision!</p>
          </div>
        </div>
      </div>
    );
  }
}

export default FilterPage;
